import { useOutletContext } from 'react-router-dom';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';

// The slice of the host app's dashboard outlet context this hook reads —
// admin and user both provide it.
type ContextProps = {
  getUserAccessByComponent: (component: AccessComponentEnum) => AccessLevelEnum;
};

const isAtLeastRead = (level?: AccessLevelEnum) =>
  level === AccessLevelEnum.Read || level === AccessLevelEnum.Write;

/**
 * Access gates the API enforces around tasks and their linked interventions.
 * Gated fields fail with FORBIDDEN_ERROR (failing the whole query) rather
 * than resolving to null, so selections and links must be gated up front.
 * Outside an outlet context (e.g. unit tests) every gate is closed.
 */
export default function useTaskAccess() {
  const context = useOutletContext<ContextProps | undefined>();
  const getAccess = context?.getUserAccessByComponent;
  const tasksManagement = getAccess?.(AccessComponentEnum.TasksManagement);
  const tasksPerforming = getAccess?.(AccessComponentEnum.TasksPerforming);
  const interventionsManagement = getAccess?.(
    AccessComponentEnum.InterventionsManagement,
  );

  return {
    // `Task.intervention` / `TaskCompletion.interventionId`.
    withIntervention:
      isAtLeastRead(tasksManagement) && isAtLeastRead(interventionsManagement),
    // `task(id:)` — anything that opens a task.
    canOpenTask:
      isAtLeastRead(tasksManagement) ||
      tasksPerforming === AccessLevelEnum.Write,
  };
}
